// src/components/parent-dashboard/views/AssignmentDetailView.js
import React from "react";
import {AlertCircle, ArrowLeft, CheckCircle, Clock, MessageSquare} from "lucide-react";
import {formatDate} from "../utils/formatters";
import AssessmentWrapper from "../components/AssessmentWrapper";

/**
 * Assignment Detail View Component
 * Shows the details of a selected assignment and its submission data.
 * This component now receives all data as props and does not fetch it directly.
 */
const AssignmentDetailView = ({
                                  selectedChild,
                                  selectedAssignment,
                                  selectedSubmission, // *** FIX: Receives submission as a prop ***
                                  detectedGrade,
                                  handleBack,
                                  // Stage detection
                                  isPreparatory,
                                  isMiddleStage,
                                  // Observation form props (for foundational stage)
                                  observationForm,
                                  setObservationForm,
                                  handleResourceChange,
                                  handleCommentsChange,
                                  handleObservationSubmit,
                                  // Additional props for other stages
                                  handleAssessmentChange,
                                  handleSupportChange,
                                  handleUnderstandingChange,
                                  // Submission callback
                                  onCaregiverObservationComplete,
                                  // UI state
                                  loading: parentLoading,
                                  error: parentError
                              }) => {

    // Use parent props for UI state
    const displayLoading = parentLoading;
    const displayError = parentError;

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    border: 1px solid #e5e7eb;
                    background: white;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .card-parent:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                
                .gradient-text {
                    background: linear-gradient(135deg, #059669, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
            `}</style>

            {/* Header */}
            <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleBack}
                                className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors flex items-center"
                            >
                                <ArrowLeft size={16} className="mr-2"/>
                                Back to Activities
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-white">
                                    {selectedAssignment?.title || "Assignment Details"}
                                </h1>
                                <p className="text-green-100">
                                    {selectedChild?.name}'s assignment feedback and progress
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Assignment Info Card */}
                {selectedAssignment && (
                    <div className="bg-white rounded-3xl shadow-xl p-6 card-parent mb-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                    <span className="text-white font-bold text-xl">
                                        {selectedAssignment.title ? selectedAssignment.title.charAt(0).toUpperCase() : 'A'}
                                    </span>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold gradient-text">
                                        {selectedAssignment.title || "Assignment"}
                                    </h2>
                                    <p className="text-gray-600 font-medium">
                                        {selectedAssignment.domain || "Subject"} • Grade {detectedGrade || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-6">
                                {selectedAssignment.due_date && (
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-orange-600">
                                            {formatDate(selectedAssignment.due_date)}
                                        </div>
                                        <div className="text-sm text-gray-600">Due Date</div>
                                    </div>
                                )}
                                <div className="text-center">
                                    <div className="text-lg font-bold text-blue-600">
                                        {isMiddleStage ? 'Middle' : isPreparatory ? 'Preparatory' : 'Foundational'}
                                    </div>
                                    <div className="text-sm text-gray-600">Stage</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error message */}
                {displayError && (
                    <div className="mb-8 bg-white rounded-3xl shadow-xl p-6 card-parent">
                        <div className="flex items-center">
                            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                                <AlertCircle className="h-6 w-6 text-red-500"/>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-red-800 mb-1">Error Loading Assignment</h3>
                                <p className="text-red-600">{displayError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {displayLoading && (
                    <div className="bg-white rounded-3xl shadow-xl p-12 card-parent text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Assignment Details</h3>
                        <p className="text-gray-600">Please wait while we fetch the latest information...</p>
                    </div>
                )}

                {/* Content sections */}
                {!displayLoading && selectedAssignment && (
                    <div className="space-y-8">
                        {/* Submission Status */}
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden card-parent">
                            <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-blue-50 border-b border-gray-100">
                                <h2 className="text-xl font-bold gradient-text">Submission Status</h2>
                            </div>
                            <div className="p-6">
                                {!selectedSubmission ? (
                                    <div className="bg-orange-50 p-6 rounded-2xl border border-orange-200">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mr-4">
                                                <Clock className="h-6 w-6 text-orange-500"/>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-orange-800 mb-1">No Submission Yet</h3>
                                                <p className="text-orange-700">Your child hasn't submitted this assignment yet.</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                                                <CheckCircle className="h-6 w-6 text-green-500"/>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-green-800 mb-1">Assignment Submitted</h3>
                                                <p className="text-green-700">Submitted on {formatDate(selectedSubmission.submitted_at)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Assessment Wrapper - This renders all the assessment components */}
                        <AssessmentWrapper
                            submission={selectedSubmission}
                            assignmentId={selectedAssignment.assignment_id || selectedAssignment.id}
                            childId={selectedChild?.student_id || selectedChild?.id}
                            isPreparatory={isPreparatory}
                            isMiddleStage={isMiddleStage}
                            onCaregiverObservationComplete={onCaregiverObservationComplete}

                            // Props for Foundational CaregiverObservation
                            observationForm={observationForm}
                            setObservationForm={setObservationForm}
                            handleResourceChange={handleResourceChange}
                            handleCommentsChange={handleCommentsChange}
                            handleObservationSubmit={handleObservationSubmit}

                            // Props for other stages
                            handleAssessmentChange={handleAssessmentChange}
                            handleSupportChange={handleSupportChange}
                            handleUnderstandingChange={handleUnderstandingChange}
                        />

                        {/* Teacher's Feedback */}
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden card-parent">
                            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100">
                                <h2 className="text-xl font-bold gradient-text">Teacher's Feedback</h2>
                            </div>
                            <div className="p-6">
                                {!selectedSubmission || !selectedSubmission.teacher_feedback ? (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                                            <MessageSquare className="h-8 w-8 text-gray-400"/>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-2">No Feedback Yet</h3>
                                        <p className="text-gray-600">Teacher feedback will appear here once the assignment is reviewed.</p>
                                    </div>
                                ) : (
                                    <div className="bg-blue-50 p-6 rounded-2xl border border-blue-200">
                                        <div className="flex items-start">
                                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                                                <MessageSquare className="h-6 w-6 text-blue-500"/>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-blue-800 mb-2">Teacher's Comments</h3>
                                                <p className="text-blue-700 leading-relaxed">{selectedSubmission.teacher_feedback}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignmentDetailView;
